import MerchandiseSearchFormResultItem from './MerchandiseSearchFormResultItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const ResultsGrid = styled.div`
  display: grid;

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(40)}
  }
`

interface Props {
  text?: string
  results: App.MerchandiseSearchTypeahead
  loading?: boolean
  setClosed?: () => void
}

function MerchandiseSearchFormResults(props: Props) {
  const { results, text, loading, setClosed } = props

  const trackingProductConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'product'), { key: 'product' },
  )
  const trackingCategoryConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'category'), { key: 'category' },
  )
  const trackingBrandConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'brand'), { key: 'brand' },
  )

  return <VerticalSpacer gap={24}>
    {text && <Caption variant="large" weight="bold">Results for "{text}"</Caption>}
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended products</Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingProductConfig}>
          {take(results.products, 3).map((product, index) => (
            <MerchandiseSearchFormResultItem
              key={product.id}
              itemId={product.id}
              type="product"
              index={index}
              setClosed={setClosed}
            />
          ))}

          {!results.products.length && <Caption variant="large">
            Sorry, we don't have any products that relate to "{text}"
          </Caption>}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended categories</Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingCategoryConfig}>
          {take(results.categories, 3).map((category, index) => (
            <MerchandiseSearchFormResultItem
              key={category.id}
              itemId={category.id}
              type="category"
              index={index}
            />
          ))}
        </OfferListEventsProvider>

        {!results.categories.length && <Caption variant="large">
          Sorry, we don't have any categories that relate to "{text}"
        </Caption>}
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">Recommended brands</Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingBrandConfig}>
          {take(results.brands, 3).map((brand, index) => (
            <MerchandiseSearchFormResultItem
              key={brand.id}
              itemId={brand.id}
              type="brand"
              index={index}
            />
          ))}
        </OfferListEventsProvider>

        {!results.brands.length && <Caption variant="large">
          Sorry, we don't have any brands that relate to "{text}"
        </Caption>}
      </ResultsGrid>
    </VerticalSpacer>
  </VerticalSpacer>
}

export default MerchandiseSearchFormResults
